import * as z from 'zod'
import type { TemplateSchema } from '../types'

type ValidationSuccess = {
  success: true
  data: Record<string, unknown>
}

type ValidationFailure = {
  success: false
  errors: string[]
}

export type TemplateValidationResult = ValidationSuccess | ValidationFailure

const isNonEmptyValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return false
  if (typeof value === 'string') return value.trim().length > 0
  return String(value).trim().length > 0
}

const buildRequiredFieldSchema = (field: string, prop: Record<string, unknown>) => {
  const label = (prop.title as string | undefined) || field

  if (prop.type === 'string') {
    let validator = z
      .string({ message: `${label} é obrigatório` })
      .trim()
      .min(1, `${label} é obrigatório`)

    const minLength = prop.minLength as number | undefined
    const maxLength = prop.maxLength as number | undefined
    const pattern = prop.pattern as string | undefined
    const enumValues = prop.enum as unknown[] | undefined

    if (typeof minLength === 'number' && minLength > 1) {
      validator = validator.min(minLength, `${label} deve ter pelo menos ${minLength} caracteres`)
    }

    if (typeof maxLength === 'number') {
      validator = validator.max(maxLength, `${label} deve ter no máximo ${maxLength} caracteres`)
    }

    if (typeof pattern === 'string') {
      const regex = new RegExp(pattern)
      validator = validator.regex(regex, `${label} está em formato inválido`)
    }

    if (Array.isArray(enumValues)) {
      validator = validator.refine((val) => val !== '', `${label} é obrigatório`)
    }

    return validator
  }

  return z.any().refine((val) => isNonEmptyValue(val), `${label} é obrigatório`)
}

/**
 * Valida dados de formulário de template, garantindo que campos obrigatórios não fiquem vazios.
 */
export const validateTemplateFormData = (
  templateSchema: TemplateSchema,
  data: Record<string, unknown>
): TemplateValidationResult => {
  const requiredFields = Array.isArray(templateSchema.schema.required)
    ? templateSchema.schema.required
    : []

  if (requiredFields.length === 0) {
    return { success: true, data }
  }

  const properties = (templateSchema.schema as { properties?: Record<string, Record<string, unknown>> }).properties || {}
  const shape: Record<string, z.ZodTypeAny> = {}

  requiredFields.forEach((field) => {
    const prop = properties[field] || {}
    shape[field] = buildRequiredFieldSchema(field, prop)
  })

  const validator = z.object(shape).passthrough()
  const result = validator.safeParse(data)

  if (!result.success) {
    const errors = result.error.issues.map((issue: { message: any }) => issue.message)
    return { success: false, errors }
  }

  return { success: true, data: result.data }
}
